import { StyleSheet, View } from 'react-native';

import { useEstimatedPointsEarned } from '@fhs/loyalty/src/queries/loyalty.queries';
import { Text } from '@fhs/ui';
import { TempCartPriceMismatchLogger } from '@fhs-legacy/frontend/src/state/cart/temp-cart-price-mismatch-logger';
import { CART_SUBTOTAL } from '@fhs-legacy/frontend/src/utils/test-ids';

import { useCartPriceOrder } from '../../../hooks/use-cart-price-order';
import { EstimatedPointsEarned } from '../../estimated-points-earned';
import { OriginalPriceDisplay } from '../item/item-profile/item-price/item-price-components';

type CartSubtotalsProps = {
  baseTotal: number;
  grandTotal: number;
};

export function CartSubtotals({ baseTotal = 0, grandTotal = 0 }: CartSubtotalsProps) {
  const { serverOrder, incentiveApplied } = useCartPriceOrder();
  const formattedGrandTotal = grandTotal < 0 ? '0.00' : (grandTotal / 100).toFixed(2);
  const { data: estimatedPoints, isPending: isLoadingPoints, enabled: isPointsEnabled } = useEstimatedPointsEarned();

  return (
    <View style={styles.container}>
      <View
        style={styles.subtotalContainer}
        accessibilityRole="text"
        accessibilityLabel={`Subtotal: ${formattedGrandTotal}${incentiveApplied && baseTotal !== grandTotal ? '. Discount applied' : ''}`}
      >
        <Text.Heading type="four">Subtotal</Text.Heading>
        <View style={styles.priceContainer}>
          {incentiveApplied && baseTotal !== grandTotal && (
            <OriginalPriceDisplay subtotalID price={baseTotal} shouldShowStrikeThrough />
          )}
          <Text.Heading type="four" testID={CART_SUBTOTAL}>
            ${formattedGrandTotal}
          </Text.Heading>
        </View>
      </View>
      {isPointsEnabled && (isLoadingPoints || estimatedPoints) && (
        <EstimatedPointsEarned
          totalPointsEarned={estimatedPoints?.totalPointsEarned}
          isLoading={isLoadingPoints}
        />
      )}
      <View
        style={styles.taxesAndFeesContainer}
        accessibilityRole="text"
        accessibilityLabel="Taxes and fees will be calculated at the final step"
      >
        <Text.Paragraph size="sm">Taxes and Fees</Text.Paragraph>
        <Text.Paragraph size="sm">Calculated at Final Step</Text.Paragraph>
      </View>
      <TempCartPriceMismatchLogger serverOrder={serverOrder} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 32,
  },
  subtotalContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 16,
    paddingBottom: 6,
  },
  priceContainer: {
    flexDirection: 'row',
    gap: 9,
    alignItems: 'center',
  },
  taxesAndFeesContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 12,
    paddingTop: 8
  },
});
